<template>
  <div class="document-upload-page">
    <!-- 步骤指引 -->
    <div class="steps-container">
      <a-steps :current="currentStep" class="upload-steps">
        <a-step :title="$t('document.uploadPage.createProject')" />
        <a-step :title="$t('document.uploadPage.uploadDocument')" />
        <a-step :title="$t('document.uploadPage.aiTranslation')" />
        <a-step :title="$t('document.uploadPage.completeOrAssign')" />
        <a-step :title="$t('document.uploadPage.completeOutput')" />
      </a-steps>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <a-card :title="$t('document.uploadPage.documentUpload')" :bordered="false" class="upload-card">
        <!-- 加载状态 -->
        <div v-if="initializing" class="initializing-container">
          <a-spin size="large" />
          <p style="margin-top: 16px; color: #666;">{{ $t('document.uploadPage.checkingProjectStatus') }}</p>
        </div>
        
        <!-- 项目文件夹 + 条目准备 -->
        <div class="project-info" v-else-if="!projectId">
          <div class="project-header">
            <h3>{{ $t('project.titleCombo.prepareWorkItem') }}</h3>
          </div>
          <a-form
            :model="projectForm"
            :rules="projectRules"
            layout="vertical"
            @finish="createProject"
            ref="projectFormRef"
          >
            <a-row :gutter="16">
              <a-col :span="12">
                <a-form-item :label="$t('document.uploadPage.projectTitle')" name="title">
                  <ProjectTitleCombo
                    v-model="projectForm.title"
                    v-model:project-id="lockedFolderProjectId"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item :label="$t('document.uploadPage.sourceLanguage')" name="original_language">
                  <a-select
                    v-model:value="projectForm.original_language"
                    :placeholder="$t('document.uploadPage.selectSourceLanguage')"
                    :options="sourceLanguageOptions"
                    :loading="languageLoading"
                    show-search
                    :filter-option="filterLanguageOption"
                  />
                </a-form-item>
              </a-col>
            </a-row>
            <a-row :gutter="16">
              <a-col :span="12">
                <a-form-item :label="$t('project.titleCombo.targetLanguage')" name="target_language">
                  <a-select
                    v-model:value="projectForm.target_language"
                    :placeholder="$t('project.titleCombo.selectTargetLanguage')"
                    :options="targetLanguageOptions"
                    :loading="languageLoading"
                    show-search
                    :filter-option="filterLanguageOption"
                  />
                </a-form-item>
              </a-col>
            </a-row>
            <a-form-item>
              <a-space>
                <a-button type="primary" html-type="submit" :loading="creating">
                  {{ $t('project.titleCombo.prepareWorkItem') }}
                </a-button>
                <a-button @click="$router.back()">{{ $t('common.cancel') }}</a-button>
              </a-space>
            </a-form-item>
          </a-form>
        </div>

        <!-- 文档上传区域 -->
        <div class="upload-section" v-else>
          <div class="section-header">
            <h3>
              <file-text-outlined />
              {{ $t('document.uploadPage.documentFileUpload') }}
            </h3>
            <p class="section-description">
              {{ $t('document.uploadPage.dragUploadDescription') }}
            </p>
          </div>

          <!-- 拖拽上传区域 -->
          <div class="upload-area">
            <a-upload-dragger
              v-model:file-list="fileList"
              name="file"
              :multiple="false"
              :before-upload="beforeUpload"
              :custom-request="handleUpload"
              :show-upload-list="false"
              :disabled="uploading || !!uploadedFile"
              accept=".docx,.pptx,.xlsx"
              class="document-uploader"
              @drop="handleDrop"
            >
              <!-- 默认上传提示 -->
              <div v-if="!uploadedFile && !uploading" class="upload-placeholder">
                <p class="upload-icon">
                  <file-text-outlined />
                </p>
                <p class="upload-text">{{ $t('document.uploadPage.clickOrDragToUpload') }}</p>
                <div class="upload-hint">
                  <p><strong>{{ $t('document.uploadPage.supportedFormats') }}：</strong>{{ $t('document.uploadPage.formatsList') }}</p>
                  <p><strong>{{ $t('document.uploadPage.fileExtensions') }}：</strong>{{ $t('document.uploadPage.extensionsList') }}</p>
                  <p><strong>{{ $t('document.uploadPage.fileSize') }}：</strong>{{ $t('document.uploadPage.noSizeLimit') || '无限制（大于10MB自动使用批量翻译）' }}</p>
                  <p><strong>{{ $t('document.uploadPage.autoProcessing') }}：</strong>{{ $t('document.uploadPage.autoProcessDescription') }}</p>
                </div>
              </div>

              <!-- 上传进度 -->
              <div v-if="uploading" class="upload-progress">
                <a-progress
                  type="circle"
                  :percent="uploadProgress"
                  :width="100"
                  :stroke-color="progressColor"
                />
                <div class="progress-info">
                  <p class="progress-text">{{ uploadStatusText }}</p>
                  <p class="progress-detail">{{ uploadDetailText }}</p>
                  <div class="progress-steps" v-if="processingSteps.length > 0">
                    <a-steps
                      :current="currentStepIndex"
                      size="small"
                      direction="vertical"
                      class="mini-steps"
                    >
                      <a-step
                        v-for="(step, index) in processingSteps"
                        :key="index"
                        :title="step.title"
                        :description="step.description"
                        :status="getStepStatus(index)"
                      />
                    </a-steps>
                  </div>
                </div>
              </div>

              <!-- 上传成功 -->
              <div v-if="uploadedFile && !uploading" class="upload-success">
                <div class="success-content">
                  <div class="file-preview">
                    <div class="file-icon">
                      <file-text-outlined />
                    </div>
                    <div class="file-info">
                      <h4 class="file-name">{{ uploadedFile.filename }}</h4>
                      <div class="file-details">
                        <a-descriptions size="small" :column="2">
                          <a-descriptions-item :label="$t('document.uploadPage.fileSizeLabel')">
                            {{ formatFileSize(uploadedFile.file_size) }}
                          </a-descriptions-item>
                          <a-descriptions-item :label="$t('document.uploadPage.uploadTime')">
                            {{ formatTime(uploadedFile.upload_time) }}
                          </a-descriptions-item>
                          <a-descriptions-item :label="$t('document.uploadPage.documentType')" v-if="uploadedFile.document_type">
                            {{ getDocumentTypeText(uploadedFile.document_type) }}
                          </a-descriptions-item>
                          <a-descriptions-item :label="$t('document.uploadPage.storagePath')">
                            <a-tag color="blue">{{ $t('document.uploadPage.localStorageTag') }}</a-tag>
                          </a-descriptions-item>
                        </a-descriptions>
                      </div>
                    </div>
                  </div>
                  <div class="file-actions" v-if="projectId">
                    <a-space direction="vertical">
                      <a-button type="primary" ghost @click="goToNextStep">
                        <right-outlined /> {{ $t('document.uploadPage.nextStep') }}
                      </a-button>
                      <a-button danger @click="clearDocumentFile">
                        <delete-outlined /> {{ $t('document.uploadPage.reupload') }}
                      </a-button>
                    </a-space>
                  </div>
                </div>
              </div>
            </a-upload-dragger>
          </div>
        </div>
      </a-card>
    </div>

    <!-- 引导模态框 -->
    <a-modal
      v-model:open="showGuideModal"
      :title="$t('document.uploadPage.guideTitle')"
      :footer="null"
      width="600px"
      class="guide-modal"
    >
      <div class="guide-content">
        <a-steps :current="guideStep" direction="vertical" size="small">
          <a-step :title="$t('document.uploadPage.guideStep1')" :description="$t('document.uploadPage.guideStep1Desc')" />
          <a-step :title="$t('document.uploadPage.guideStep2')" :description="$t('document.uploadPage.guideStep2Desc')" />
          <a-step :title="$t('document.uploadPage.guideStep3')" :description="$t('document.uploadPage.guideStep3Desc')" />
          <a-step :title="$t('document.uploadPage.guideStep4')" :description="$t('document.uploadPage.guideStep4Desc')" />
          <a-step :title="$t('document.uploadPage.guideStep5')" :description="$t('document.uploadPage.guideStep5Desc')" />
        </a-steps>
        <div class="guide-actions">
          <a-space>
            <a-button @click="showGuideModal = false">{{ $t('document.uploadPage.close') }}</a-button>
            <a-button type="primary" @click="startGuide">{{ $t('document.uploadPage.startUsing') }}</a-button>
          </a-space>
        </div>
      </div>
    </a-modal>

  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import {
  FileTextOutlined,
  RightOutlined,
  DeleteOutlined
} from '@ant-design/icons-vue'
import type { UploadProps, FormInstance } from 'ant-design-vue'
import { uploadDocumentToWorkItem, deleteDocumentFile } from '@/modules/document/api'
import { ProjectType, getProject } from '@/modules/project/api'
import { createWorkItem, getWorkItem } from '@/modules/project/api/work-item'
import ProjectTitleCombo from '@/modules/project/components/ProjectTitleCombo.vue'
import { resolveProjectForUpload } from '@/modules/project/composables/resolveProjectForUpload'
import { loadUploadEntryContext, toUploadPageEntryState } from '@/modules/project/utils/upload-entry-query'
import { languageApi, LanguageType } from '@/modules/system/api/language'
import { runApiBatch } from '@/shared/api/use-api-batch'
import { formatFileSize } from '@/shared/utils'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

// 响应式数据
const currentStep = ref(0)
const projectId = ref<number>()
const creating = ref(false)
const languageLoading = ref(false)
const uploading = ref(false)
const uploadProgress = ref(0)
const currentProcessingStep = ref<{ title: string; description: string }>({ 
  title: t('document.uploadPage.uploadFile'), 
  description: t('document.uploadPage.uploadingDocument') 
})
const currentStepIndex = ref(0)
const initializing = ref(true)

// 表单相关
const projectFormRef = ref<FormInstance>()
const lockedFolderProjectId = ref<number | null>(null)
const projectForm = reactive({
  title: '',
  original_language: '',
  target_language: undefined as string | undefined,
})

const projectRules = computed(() => ({
  title: [
    { required: true, message: t('project.titleCombo.titleRequired'), trigger: 'blur' },
    { min: 2, max: 100, message: t('document.uploadPage.titleLength'), trigger: 'blur' }
  ],
  original_language: [
    { required: true, message: t('document.uploadPage.sourceLanguageRequired'), trigger: 'change' }
  ],
  target_language: [
    { required: true, message: t('project.titleCombo.selectTargetLanguage'), trigger: 'change' }
  ],
}))

// 文件相关
const fileList = ref([])
const uploadedFile = ref<any>()

// 语言选项
const sourceLanguageOptions = ref<Array<{ label: string; value: string }>>([])
const targetLanguageOptions = ref<Array<{ label: string; value: string }>>([])

// 处理步骤
const processingSteps = computed(() => [
  { title: t('document.uploadPage.uploadFile'), description: t('document.uploadPage.uploadingDocument') },
  { title: t('document.uploadPage.validateFormat'), description: t('document.uploadPage.validatingFormat') },
  { title: t('document.uploadPage.extractContent'), description: t('document.uploadPage.extractingContent') },
  { title: t('document.uploadPage.localStorage'), description: t('document.uploadPage.savingToLocal') },
  { title: t('document.uploadPage.completeProcessing'), description: t('document.uploadPage.fileProcessingComplete') }
])

// 引导相关
const showGuideModal = ref(false)
const guideStep = ref(0)

// 生命周期
async function bootstrapFromRouteQuery() {
  const state = toUploadPageEntryState(
    await loadUploadEntryContext(route.query, {
      getProject,
      getWorkItem,
    }),
  )
  if (state.errorLog) {
    console.error(state.errorLog.message, state.errorLog.error)
  }
  projectId.value = state.projectId
  currentStep.value = state.currentStep
  lockedFolderProjectId.value = state.lockedFolderProjectId
  if (state.title) projectForm.title = state.title
  if (state.original_language) projectForm.original_language = state.original_language
  if (state.target_language) projectForm.target_language = state.target_language
}

onMounted(async () => {
  try {
    await loadLanguageOptions()
    await bootstrapFromRouteQuery()

    const hasSeenGuide = localStorage.getItem('document_upload_guide_seen')
    if (!hasSeenGuide && lockedFolderProjectId.value == null && !projectId.value) {
      showGuideModal.value = true
    }
  } finally {
    initializing.value = false
  }
})

// 方法
const loadLanguageOptions = async () => {
  try {
    languageLoading.value = true
    const [sourceResult, targetResult] = await runApiBatch([
      (ctx) =>
        languageApi.getLanguageOptions(
          {
            language_type: LanguageType.SOURCE,
            status: true,
          },
          ctx.requestConfig,
        ),
      (ctx) =>
        languageApi.getLanguageOptions(
          {
            language_type: LanguageType.TARGET,
            status: true,
          },
          ctx.requestConfig,
        ),
    ])
    if (sourceResult.status === 'fulfilled') {
      sourceLanguageOptions.value = sourceResult.value.map((option) => ({
        label: option.label,
        value: option.value,
        key: option.value,
      }))
    }
    if (targetResult.status === 'fulfilled') {
      targetLanguageOptions.value = targetResult.value.map((option) => ({
        label: option.label,
        value: option.value,
        key: option.value,
      }))
    }
  } catch (error) {
    console.error('获取语言选项失败:', error)
  } finally {
    languageLoading.value = false
  }
}

const filterLanguageOption = (input: string, option: any) => {
  return option.label.toLowerCase().includes(input.toLowerCase())
}

const createProject = async () => {
  try {
    creating.value = true
    
    if (!projectForm.title || projectForm.title.trim().length === 0) {
      message.error(t('project.titleCombo.titleRequired'))
      return
    }

    const folderId = await resolveProjectForUpload({
      title: projectForm.title,
      lockedProjectId: lockedFolderProjectId.value,
    })
    const workItem = await createWorkItem({
      project_id: folderId,
      item_type: ProjectType.DOCUMENT,
      target_language: projectForm.target_language,
      source_language: projectForm.original_language || 'zh',
      file_name: projectForm.title.trim(),
    })

    projectId.value = workItem.id
    currentStep.value = 1
    message.success(t('project.titleCombo.workItemReady'))
  } catch (error: unknown) {
    console.error('准备条目失败:', error)
  } finally {
    creating.value = false
  }
}

const beforeUpload: UploadProps['beforeUpload'] = (file) => {
  if (!/\.(docx|pptx|xlsx)$/i.test(file.name)) {
    message.error('文件格式不支持！只支持以下格式：.pptx、.docx、.xlsx')
    return false
  }
  return true
}

const handleUpload = async (options: any) => {
  const { file } = options

  if (!projectId.value) {
    message.error(t('document.uploadPage.pleaseCreateProjectFirst'))
    return
  }

  try {
    uploading.value = true
    uploadProgress.value = 0
    currentProcessingStep.value = processingSteps.value[0]
    currentStepIndex.value = 0

    const stepInterval = setInterval(() => {
      if (currentStepIndex.value < processingSteps.value.length - 1) {
        currentStepIndex.value++
        currentProcessingStep.value = processingSteps.value[currentStepIndex.value]
      } else {
        clearInterval(stepInterval)
      }
    }, 1000)
    
    // 上传文档文件到项目
    const response = await uploadDocumentToWorkItem(projectId.value, file)
    
    if (response.success) {
      uploadedFile.value = {
        filename: file.name,
        file_size: response.file_size,
        file_url: response.file_url,
        upload_time: new Date().toISOString(),
        document_type: getDocumentTypeFromFilename(file.name)
      }
      uploadProgress.value = 100
      currentStepIndex.value = processingSteps.value.length - 1
      currentProcessingStep.value = processingSteps.value[currentStepIndex.value]
      message.success(t('document.uploadPage.documentUploadSuccess'))
    } else {
      console.error('文档上传失败:', response.message)
      message.error(response.message || t('document.uploadPage.fileUploadFailed'))
    }
  
} catch (error: any) {
  console.error('上传失败:', error)
  } finally {
    uploading.value = false
  }
}

// 计算属性
const progressColor = computed(() => {
  if (uploadProgress.value < 30) return '#ff4d4f'
  if (uploadProgress.value < 70) return '#faad14'
  return '#52c41a'
})

const uploadStatusText = computed(() => {
  if (uploadProgress.value < 30) return t('document.uploadPage.uploadingFile')
  if (uploadProgress.value < 70) return t('document.uploadPage.processingFile')
  if (uploadProgress.value < 100) return t('document.uploadPage.almostDone')
  return t('document.uploadPage.uploadComplete')
})

const uploadDetailText = computed(() => {
  return currentProcessingStep.value.description
})

/** 上传完成后直接进入机翻工作区（不再强制先指派 Task） */
const goToNextStep = () => {
  if (!projectId.value) {
    message.error(t('document.uploadPage.pleaseCreateProjectFirst'))
    return
  }
  router.push({
    name: 'DocumentTranslation',
    params: { id: String(projectId.value) },
    query: { target_language: projectForm.target_language },
  })
}

const clearDocumentFile = async () => {
  try {
    if (uploadedFile.value && projectId.value) {
      try {
        await deleteDocumentFile(projectId.value)
      } catch (error) {
        console.warn('删除服务器文件失败:', error)
      }
    }

    fileList.value = []
    uploadedFile.value = null
    uploadProgress.value = 0
    currentStepIndex.value = 0
    currentProcessingStep.value = processingSteps.value[0]

    message.success(t('document.uploadPage.fileCleared'))
  } catch (error) {
    console.error('清除文件失败:', error)
  }
}

const handleDrop = (e: DragEvent) => {
  e.preventDefault()
}

const getStepStatus = (index: number) => {
  if (index < currentStepIndex.value) return 'finish'
  if (index === currentStepIndex.value) return 'process'
  return 'wait'
}

const getDocumentTypeFromFilename = (filename: string): string => {
  const ext = filename.toLowerCase().split('.').pop()
  switch (ext) {
    case 'doc':
    case 'docx':
      return 'word'
    case 'ppt':
    case 'pptx':
      return 'ppt'
    case 'xls':
    case 'xlsx':
      return 'excel'
    case 'txt':
      return 'txt'
    case 'md':
      return 'md'
    default:
      return 'word' // 默认返回word类型
  }
}

const getDocumentTypeText = (type: string) => {
  const typeMap: Record<string, string> = {
    'word': t('document.uploadPage.wordDocument'),
    'powerpoint': t('document.uploadPage.powerpoint'),
    'excel': t('document.uploadPage.excel'),
    'text': t('document.uploadPage.plainText'),
    'markdown': t('document.uploadPage.markdown'),
    'document': t('document.uploadPage.document')
  }
  return typeMap[type] || t('document.uploadPage.unknownType')
}

const formatTime = (timeString: string) => {
  return new Date(timeString).toLocaleString()
}

const startGuide = () => {
  showGuideModal.value = false
  localStorage.setItem('document_upload_guide_seen', 'true')
}
</script>

<style lang="less" scoped>
.document-upload-page {
  padding: 24px;
  background: #f5f5f5;
  min-height: calc(100vh - 64px);

  .steps-container {
    margin-bottom: 24px;
    
    .upload-steps {
      background: white;
      padding: 24px;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }

  .main-content {
    .upload-card {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      border-radius: 12px;
      
      :deep(.ant-card-body) {
        padding: 32px;
      }
    }
  }

  .initializing-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
  }

  .project-info {
    margin-bottom: 32px;
    padding-bottom: 32px;
    
    .project-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      
      h3 {
        margin: 0;
        color: #1890ff;
        font-size: 18px;
        font-weight: 600;
      }
    }
    
    border-bottom: 1px solid #f0f0f0;
  }

  .upload-section {
    .section-header {
      margin-bottom: 24px;
      
      h3 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
        font-size: 18px;
        font-weight: 600;
        color: #262626;
        
        .anticon {
          color: #1890ff;
        }
      }
      
      .section-description {
        color: #8c8c8c;
        margin: 0;
        line-height: 1.5;
      }
    }
  }

  .upload-area {
    margin-bottom: 32px;
    
    .document-uploader {
      :deep(.ant-upload-drag) {
        padding: 48px 24px;
        border: 2px dashed #d9d9d9;
        border-radius: 12px;
        background: #fafafa;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;

        &:hover {
          border-color: #1890ff;
          background: linear-gradient(135deg, #f0f8ff 0%, #e6f7ff 100%);
        }

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(24, 144, 255, 0.1), transparent);
          transition: left 0.6s;
        }

        &:hover::before {
          left: 100%;
        }
      }
    }
  }

  .upload-placeholder {
    text-align: center;
    
    .upload-icon {
      font-size: 64px;
      color: #1890ff;
      margin-bottom: 16px;
      display: block;
      
      &.small {
        font-size: 48px;
      }
    }
    
    .upload-text {
      font-size: 18px;
      color: #262626;
      margin-bottom: 16px;
      font-weight: 500;
    }
    
    .upload-hint {
      color: #8c8c8c;
      line-height: 1.6;
      
      p {
        margin-bottom: 4px;
        
        strong {
          color: #595959;
        }
      }
    }
    
    &.small {
      .upload-text {
        font-size: 16px;
      }
      
      .upload-hint {
        font-size: 13px;
      }
    }
  }

  .upload-progress {
    text-align: center;
    
    .progress-info {
      margin-top: 24px;
      
      .progress-text {
        font-size: 16px;
        color: #1890ff;
        margin-bottom: 8px;
        font-weight: 500;
      }
      
      .progress-detail {
        font-size: 14px;
        color: #8c8c8c;
        margin-bottom: 16px;
      }
      
      .progress-steps {
        max-width: 300px;
        margin: 0 auto;
        
        .mini-steps {
          :deep(.ant-steps-item-title) {
            font-size: 12px;
          }
          
          :deep(.ant-steps-item-description) {
            font-size: 11px;
          }
        }
      }
    }
    
    &.small {
      .progress-text {
        font-size: 14px;
        margin-top: 12px;
      }
    }
  }

  .upload-success {
    .success-content {
      display: flex;
      align-items: flex-start;
      gap: 24px;
      padding: 24px;
      background: linear-gradient(135deg, #f6ffed 0%, #f0f9ff 100%);
      border: 1px solid #b7eb8f;
      border-radius: 12px;
      
      .file-preview {
        flex: 1;
        display: flex;
        align-items: flex-start;
        gap: 16px;
        
        .file-icon {
          font-size: 48px;
          color: #52c41a;
          flex-shrink: 0;
        }
        
        .file-info {
          flex: 1;
          
          .file-name {
            font-size: 18px;
            font-weight: 600;
            color: #262626;
            margin-bottom: 12px;
            word-break: break-all;
          }
          
          .file-details {
            :deep(.ant-descriptions-item-label) {
              font-weight: 500;
              color: #595959;
            }
            
            :deep(.ant-descriptions-item-content) {
              color: #262626;
            }
          }
        }
      }
      
      .file-actions {
        flex-shrink: 0;
      }
    }
    
    &.small {
      .success-content {
        padding: 16px;
        
        .file-preview {
          .file-icon {
            font-size: 32px;
          }
          
          .file-info .file-name {
            font-size: 16px;
          }
        }
      }
    }
  }
}

// 引导弹窗样式
.guide-modal {
  .guide-content {
    .guide-step-content {
      margin-top: 8px;
      
      p {
        margin-bottom: 8px;
        color: #595959;
      }
      
      ul {
        margin: 8px 0;
        padding-left: 20px;
        
        li {
          margin-bottom: 4px;
          color: #8c8c8c;
          line-height: 1.5;
          
          strong {
            color: #262626;
          }
        }
      }
    }
    
    .guide-actions {
      margin-top: 24px;
      text-align: right;
      padding-top: 16px;
      border-top: 1px solid #f0f0f0;
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .document-upload-page {
    padding: 16px;
    
    .main-content .upload-card :deep(.ant-card-body) {
      padding: 16px;
    }
    
    .upload-success .success-content {
      flex-direction: column;
      gap: 16px;
      
      .file-actions {
        align-self: stretch;
      }
    }
  }
}
</style>
